import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { NG_STRIPE_CONFIG } from './stripe-config';

@Injectable()
export class StripeService {
  private _snackbar = inject(MatSnackBar);
  private _accounts: Map<string, Promise<Stripe | null>> = new Map();
  config = inject(NG_STRIPE_CONFIG);

  forAccount(connectedAccountId: string): Promise<Stripe | null> {
    const existingStripe = this._accounts.get(connectedAccountId);
    if (existingStripe) {
      return existingStripe;
    }
    const stripe = this._load(connectedAccountId);
    this._accounts.set(connectedAccountId, stripe);
    return stripe;
  }

  showStripeInitError(): void {
    this._snackbar.open('Unable to load Stripe');
  }

  /**
   * Stripe.js rejects when its script cannot load, such as behind an ad or
   * script blocker. Callers handle an unavailable Stripe through the `null`
   * branch, so a rejection resolves `null` instead, and the failed load is
   * not cached so a later request can try again.
   */
  private async _load(connectedAccountId: string): Promise<Stripe | null> {
    try {
      return await loadStripe(this.config.apiKey, {
        stripeAccount: connectedAccountId,
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- records why Stripe.js failed to load, which the patient only sees as "card payment is unavailable"
      console.error('Unable to load Stripe.js', error);
      this._accounts.delete(connectedAccountId);
      // eslint-disable-next-line no-null/no-null -- keeps loadStripe's contract, which resolves null when Stripe.js cannot initialise
      return null;
    }
  }
}
